/**
 * Vendor Bot Shop - Cart Page
 * Shopping cart with quantity controls, product types, API integration
 */

import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BottomNavigation,
  QuantitySelector,
  PriceDisplay
} from '../../components/customer';
import { useTranslation } from '../../hooks/useTranslation';

interface CartItem {
  id: number;
  product_id: number;
  name: string;
  price: number;
  compare_at_price: number | null;
  quantity: number;
  product_type: 'physical' | 'downloadable' | 'keys' | 'subscription';
  image_url: string | null;
  stock_quantity: number;
  track_inventory: boolean;
  variation_text?: string | null;
  selected_variations?: string | null;
}

interface StoreInfo {
  store_name: string;
  currency: string;
  currency_symbol: string;
}

interface CartSummary {
  subtotal: number;
  item_count: number;
  has_physical: boolean;
  has_digital: boolean;
}

const Cart: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [storeInfo, setStoreInfo] = useState<StoreInfo>({
    store_name: 'Store',
    currency: 'USD',
    currency_symbol: '$'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [updatingItems, setUpdatingItems] = useState<Set<number>>(new Set());

  const baseUrl = process.env.REACT_APP_API_URL || '';

  // Calculate cart summary
  const cartSummary: CartSummary = {
    subtotal: cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    item_count: cartItems.reduce((sum, item) => sum + item.quantity, 0),
    has_physical: cartItems.some(item => item.product_type === 'physical'),
    has_digital: cartItems.some(item => ['downloadable', 'keys', 'subscription'].includes(item.product_type))
  };

  // Load store info
  useEffect(() => {
    const loadStoreInfo = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/shop/${botUsername}/store-info`);
        const data = await res.json();
        if (data.success) {
          setStoreInfo(data.data);
        }
      } catch (error) {
        console.error('Error loading store info:', error);
      }
    };

    loadStoreInfo();
  }, [botUsername, baseUrl]);

  // Load cart items
  const loadCart = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.warn('No auth token for cart');
        setIsLoading(false);
        return;
      }

      const res = await fetch(`${baseUrl}/api/cart/${botUsername}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();

      if (data.success && data.data) {
        setCartItems(data.data.items || []);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setIsLoading(false);
    }
  }, [botUsername, baseUrl]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  // Update quantity
  const handleQuantityChange = async (cartItemId: number, newQuantity: number) => {
    if (newQuantity < 1) return;

    setUpdatingItems(prev => new Set(prev).add(cartItemId));

    // Optimistic update
    setCartItems(prev =>
      prev.map(item =>
        item.id === cartItemId ? { ...item, quantity: newQuantity } : item
      )
    );

    try {
      const token = localStorage.getItem('auth_token');

      const res = await fetch(`${baseUrl}/api/cart/${botUsername}/items/${cartItemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ quantity: newQuantity })
      });

      const data = await res.json();
      if (!data.success) {
        // Revert on failure
        loadCart();
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      loadCart();
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(cartItemId);
        return next;
      });
    }
  };

  // Remove item
  const handleRemoveItem = async (cartItemId: number) => {
    setUpdatingItems(prev => new Set(prev).add(cartItemId));

    // Optimistic remove
    setCartItems(prev => prev.filter(item => item.id !== cartItemId));

    try {
      const token = localStorage.getItem('auth_token');

      const res = await fetch(`${baseUrl}/api/cart/${botUsername}/items/${cartItemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await res.json();
      if (!data.success) {
        loadCart();
      }
    } catch (error) {
      console.error('Error removing item:', error);
      loadCart();
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(cartItemId);
        return next;
      });
    }
  };

  // Clear cart
  const handleClearCart = async () => {
    const tg = window.Telegram?.WebApp;
    const confirmMessage = t('cart.clearCart');

    if (tg?.showConfirm) {
      tg.showConfirm(confirmMessage, async (confirmed) => {
        if (confirmed) {
          await clearCart();
        }
      });
    } else if (window.confirm(confirmMessage)) {
      await clearCart();
    }
  };

  const clearCart = async () => {
    try {
      const token = localStorage.getItem('auth_token');

      const res = await fetch(`${baseUrl}/api/cart/${botUsername}/clear`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await res.json();
      if (data.success) {
        setCartItems([]);
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  // Initialize Telegram WebApp
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();

      if (tg.themeParams?.bg_color) {
        tg.setBackgroundColor(tg.themeParams.bg_color);
        tg.setHeaderColor(tg.themeParams.bg_color);
      }
    }
  }, []);

  // Get product type label
  const getProductTypeLabel = (type: string) => {
    switch (type) {
      case 'physical': return t('shop.productTypes.physical');
      case 'downloadable': return t('shop.productTypes.downloadable');
      case 'keys': return t('shop.productTypes.keys');
      case 'subscription': return t('shop.productTypes.subscription');
      default: return type;
    }
  };

  // Format price
  const formatPrice = (value: number) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <PageContainer>
      <Header>
        <HeaderTitle>{t('cart.title')}</HeaderTitle>
        {cartItems.length > 0 && (
          <ClearButton onClick={handleClearCart}>{t('cart.clear')}</ClearButton>
        )}
      </Header>

      <Content>
        {isLoading ? (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        ) : cartItems.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17 18c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2zM7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm0-3l1.1-2h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1v2h2l3.6 7.59L5.25 14c-.85 1.57.1 3.5 1.75 3.5h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12z"/>
              </svg>
            </EmptyIcon>
            <EmptyTitle>{t('cart.empty')}</EmptyTitle>
            <EmptyText>{t('cart.emptyMessage')}</EmptyText>
            <BrowseButton onClick={() => navigate(`/shop/${botUsername}`)}>
              {t('cart.browseProducts')}
            </BrowseButton>
          </EmptyState>
        ) : (
          <>
            <ItemCount>
              {cartSummary.item_count} {t('cart.itemsInCart')}
            </ItemCount>

            <CartList>
              {cartItems.map((item) => (
                <CartItemCard key={item.id} $isUpdating={updatingItems.has(item.id)}>
                  <ItemImageContainer onClick={() => navigate(`/shop/${botUsername}/product/${item.product_id}`)}>
                    {item.image_url ? (
                      <ItemImage src={item.image_url} alt={item.name} />
                    ) : (
                      <PlaceholderImage>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86l-3 3.87L9 13.14 6 17h12l-3.86-5.14z"/>
                        </svg>
                      </PlaceholderImage>
                    )}
                    <ProductTypeBadge $type={item.product_type}>
                      {getProductTypeLabel(item.product_type)}
                    </ProductTypeBadge>
                  </ItemImageContainer>

                  <ItemDetails>
                    <ItemName onClick={() => navigate(`/shop/${botUsername}/product/${item.product_id}`)}>
                      {item.name}
                    </ItemName>
                    {item.variation_text && (
                      <VariationText>{item.variation_text}</VariationText>
                    )}

                    <PriceDisplay
                      price={item.price}
                      originalPrice={item.compare_at_price}
                      currencySymbol={storeInfo.currency_symbol}
                      size="small"
                      showDiscount={false}
                    />

                    <ItemActions>
                      <QuantitySelector
                        quantity={item.quantity}
                        onChange={(qty) => handleQuantityChange(item.id, qty)}
                        min={1}
                        max={item.track_inventory ? item.stock_quantity : 99}
                        disabled={updatingItems.has(item.id)}
                        size="small"
                      />
                      <RemoveButton onClick={() => handleRemoveItem(item.id)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </RemoveButton>
                    </ItemActions>

                    <ItemTotal>
                      {storeInfo.currency_symbol}{formatPrice(item.price * item.quantity)}
                    </ItemTotal>
                  </ItemDetails>
                </CartItemCard>
              ))}
            </CartList>

            <SummaryCard>
              <SummaryRow>
                <SummaryLabel>{t('cart.subtotal')}</SummaryLabel>
                <SummaryValue>
                  {storeInfo.currency_symbol}{formatPrice(cartSummary.subtotal)}
                </SummaryValue>
              </SummaryRow>

              {cartSummary.has_physical && !cartSummary.has_digital && (
                <SummaryNote>{t('cart.shippingCalculated')}</SummaryNote>
              )}

              {cartSummary.has_digital && cartSummary.has_physical && (
                <SummaryNote>{t('cart.physicalAndDigital')}</SummaryNote>
              )}

              {!cartSummary.has_physical && cartSummary.has_digital && (
                <SummaryNote>{t('cart.digitalInstant')}</SummaryNote>
              )}

              <TotalRow>
                <TotalLabel>{t('cart.total')}</TotalLabel>
                <TotalValue>
                  {storeInfo.currency_symbol}{formatPrice(cartSummary.subtotal)}
                </TotalValue>
              </TotalRow>
            </SummaryCard>

            <CheckoutButton onClick={() => navigate(`/shop/${botUsername}/checkout`)}>
              {t('cart.checkout')}
            </CheckoutButton>
          </>
        )}
      </Content>

      <BottomNavigation />
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  padding-bottom: calc(70px + env(safe-area-inset-bottom, 0px));
  max-width: 768px;
  margin: 0 auto;

  @media (min-width: 768px) {
    max-width: 900px;
  }
`;

const Header = styled.header`
  background: var(--tg-theme-bg-color, #ffffff);
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--tg-theme-hint-color, #e0e0e0)20;

  @media (min-width: 375px) {
    padding: 16px;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 16px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;

  @media (min-width: 375px) {
    font-size: 18px;
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #f44336;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px;

  @media (min-width: 375px) {
    font-size: 14px;
    padding: 8px;
  }
`;

const Content = styled.main`
  padding: 12px;

  @media (min-width: 375px) {
    padding: 16px;
  }

  @media (min-width: 600px) {
    padding: 20px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--tg-theme-hint-color, #e0e0e0);
  border-top-color: var(--tg-theme-button-color, #3390ec);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  color: var(--tg-theme-hint-color, #999);
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: var(--tg-theme-hint-color, #999);
  margin: 0 0 24px 0;
`;

const BrowseButton = styled.button`
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;

const ItemCount = styled.p`
  font-size: 13px;
  color: var(--tg-theme-hint-color, #999);
  margin: 0 0 12px 0;
`;

const CartList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const CartItemCard = styled.div<{ $isUpdating: boolean }>`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 10px;
  padding: 10px;
  display: flex;
  gap: 10px;
  opacity: ${props => props.$isUpdating ? 0.6 : 1};
  transition: opacity 0.2s;

  @media (min-width: 375px) {
    border-radius: 12px;
    padding: 12px;
    gap: 12px;
  }
`;

const ItemImageContainer = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  cursor: pointer;

  @media (min-width: 375px) {
    width: 80px;
    height: 80px;
  }

  @media (min-width: 600px) {
    width: 90px;
    height: 90px;
  }
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const PlaceholderImage = styled.div`
  width: 100%;
  height: 100%;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tg-theme-hint-color, #999);
`;

const ProductTypeBadge = styled.span<{ $type: string }>`
  position: absolute;
  top: 4px;
  left: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  color: white;
  background: ${props => {
    switch (props.$type) {
      case 'physical': return '#4CAF50';
      case 'downloadable': return '#2196F3';
      case 'keys': return '#9C27B0';
      case 'subscription': return '#FF9800';
      default: return '#757575';
    }
  }};
`;

const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ItemName = styled.h3`
  font-size: 13px;
  font-weight: 500;
  color: var(--tg-theme-text-color, #000000);
  margin: 0 0 4px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  @media (min-width: 375px) {
    font-size: 14px;
  }
`;

const VariationText = styled.span`
  font-size: 11px;
  color: var(--tg-theme-hint-color, #999);
  margin-bottom: 4px;
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #f44336;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemTotal = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin-top: auto;
  text-align: right;
`;

const SummaryCard = styled.div`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;

  @media (min-width: 375px) {
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SummaryLabel = styled.span`
  font-size: 14px;
  color: var(--tg-theme-hint-color, #999);
`;

const SummaryValue = styled.span`
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  font-weight: 500;
`;

const SummaryNote = styled.p`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  margin: 8px 0;
  font-style: italic;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  margin-top: 12px;
  border-top: 1px solid var(--tg-theme-hint-color, #e0e0e0)40;
`;

const TotalLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
`;

const TotalValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: var(--tg-theme-text-color, #000000);

  @media (min-width: 375px) {
    font-size: 20px;
  }
`;

const CheckoutButton = styled.button`
  width: 100%;
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  border: none;
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:active {
    opacity: 0.8;
  }

  @media (min-width: 375px) {
    padding: 14px;
    font-size: 16px;
  }
`;

export default Cart;
